// STUDIO-TIMER-EXTERNAL-NOTICE-PRESET-1 · 타이머 문구 5개 + 외부송출용 공지 5개 (TEST 공유 세션 · Studio·콘솔 공용).
// STUDIO-NOTICE-EXTERNAL-UI-REORGANIZE-1 · 외부송출용 공지를 타이머에서 분리 · 텍스트+이미지 통합 최대 5개 · 1개만 선택.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Clock, Megaphone, Pencil, Play, Plus, Trash2, RefreshCw, Check, X } from 'lucide-react';
import api from '@/lib/api';
import { timerViewOf } from '@/lib/timerLocal';
import { PRESET_MAX, presetOps, noticeType } from '@/lib/testLiveShare';

const chip = 'min-h-[32px] px-2 rounded-lg border border-black/10 dark:border-white/10 text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40';
const inputCls = 'flex-1 min-w-[7rem] px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold';

const PresetForm = ({ init, onSave, onCancel, testid }) => {
  const [label, setLabel] = useState(init?.label || '');
  const [minutes, setMinutes] = useState(init?.minutes ?? 5);
  const save = () => {
    const m = Number(minutes);
    if (!label.trim()) { toast.error('문구를 입력해주세요.'); return; }
    if (!(m >= 0.5 && m <= 180)) { toast.error('시간은 0.5~180분이에요.'); return; }
    onSave(label.trim().slice(0, 24), m);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      <input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 24))} placeholder="예: 특가 마감"
             className={inputCls} data-testid={`${testid}-label`} />
      <input type="number" min={0.5} max={180} step={0.5} value={minutes} onChange={(e) => setMinutes(e.target.value)}
             className="w-14 px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold"
             data-testid={`${testid}-minutes`} />
      <span className="text-[10px] text-gray-500">분</span>
      <button type="button" onClick={save} className={`${chip} bg-black text-white dark:bg-white dark:text-black`} data-testid={`${testid}-save`}><Check size={12} />저장</button>
      <button type="button" onClick={onCancel} className={chip} data-testid={`${testid}-cancel`}>취소</button>
    </div>
  );
};

export const TimerPresetList = ({ ctx, apply, canStart, onStartTimer }) => {
  const [mode, setMode] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const tStatus = ctx?.timer?.status;
  useEffect(() => {
    if (tStatus !== 'running') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tStatus]);
  const tv = timerViewOf(ctx?.timer, now);
  const list = ctx?.timer_presets || [];
  const full = list.length >= PRESET_MAX;
  const start = (p) => {
    if (!canStart) { toast.error('테스트 LIVE 중에 시작할 수 있어요.'); return; }
    if (onStartTimer) onStartTimer(p);
    else apply((c) => presetOps.timer(c, 'start', { duration_seconds: Math.round(p.minutes * 60), label: p.label }));
    toast.success(`⏱ ${p.label} 시작`);
  };
  return (
    <div className="space-y-1.5" data-testid="timer-preset-list">
      <p className="text-[10px] font-black text-gray-500 inline-flex items-center gap-1"><Clock size={11} /> 저장 문구 {list.length}/{PRESET_MAX}</p>
      {list.map((p) => (mode === p.id ? (
        <PresetForm key={p.id} init={p} testid={`timer-preset-edit-${p.id}`} onCancel={() => setMode(null)}
                    onSave={(label, minutes) => { apply((c) => presetOps.updateTimerPreset(c, p.id, { label, minutes })); setMode(null); }} />
      ) : (
        <div key={p.id} className="flex items-center gap-1" data-testid={`timer-preset-${p.id}`}>
          <span className="flex-1 min-w-0 truncate text-[11px] font-bold">{p.label} · {p.minutes}분</span>
          {tv && ctx?.timer?.label === p.label && (
            <span className="shrink-0 text-[10px] font-black text-[#FF3B30]" data-testid={`timer-preset-running-${p.id}`}>
              {tv.status === 'paused' ? '일시정지' : tv.status === 'finished' ? '종료' : '진행 중'} {tv.display}
            </span>
          )}
          <button type="button" onClick={() => start(p)} className={`${chip} text-[#FF3B30] border-[#FF3B30]/40`} data-testid={`timer-preset-start-${p.id}`}><Play size={11} />시작</button>
          <button type="button" onClick={() => setMode(p.id)} className={chip} aria-label="수정" data-testid={`timer-preset-edit-btn-${p.id}`}><Pencil size={11} /></button>
          <button type="button" onClick={() => apply((c) => presetOps.removeTimerPreset(c, p.id))} className={chip} aria-label="삭제" data-testid={`timer-preset-delete-${p.id}`}><Trash2 size={11} /></button>
        </div>
      )))}
      {mode === 'new' && (
        <PresetForm testid="timer-preset-new" onCancel={() => setMode(null)}
                    onSave={(label, minutes) => { apply((c) => presetOps.addTimerPreset(c, label, minutes)); setMode(null); }} />
      )}
      {mode !== 'new' && (
        <button type="button" onClick={() => (full ? toast.warning(`문구는 최대 ${PRESET_MAX}개까지 저장할 수 있어요.`) : setMode('new'))}
                className={`${chip} ${full ? 'opacity-50' : ''}`} data-testid="timer-preset-add"><Plus size={12} />문구 추가</button>
      )}
    </div>
  );
};

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', 'broadcast');
  const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

const TextForm = ({ init = '', onSave, onCancel, testid }) => {
  const [text, setText] = useState(init);
  const save = () => { if (!text.trim()) { toast.error('공지 문구를 입력해주세요.'); return; } onSave(text.trim().slice(0, 80)); };
  return (
    <div className="flex items-center gap-1 w-full" data-testid={testid}>
      <input value={text} onChange={(e) => setText(e.target.value.slice(0, 80))} placeholder="예: 오늘 전 상품 무료배송"
             className={inputCls} data-testid={`${testid}-text`} />
      <button type="button" onClick={save} className={`${chip} bg-black text-white dark:bg-white dark:text-black`} data-testid={`${testid}-save`}><Check size={12} />저장</button>
      <button type="button" onClick={onCancel} className={chip} data-testid={`${testid}-cancel`}>취소</button>
    </div>
  );
};

const NoticeItem = ({ n, selected, busy, onSelect, onEdit, onReplace, onDelete }) => {
  const img = noticeType(n) === 'image';
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={`external-notice-${n.id}`} data-type={noticeType(n)}>
      <button type="button" onClick={onSelect}
              className={`relative ${img ? 'w-14' : 'w-24 px-1.5'} h-14 rounded-lg overflow-hidden border-2 text-[10px] font-bold leading-tight text-left ${selected ? 'border-[#FF3B30]' : 'border-black/10 dark:border-white/10'}`}
              data-testid={`external-notice-select-${n.id}`} data-selected={selected ? '1' : '0'}>
        {img
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${n.url}`} alt="" className="w-full h-full object-cover" />
          : <span className="line-clamp-3 break-all" data-testid={`external-notice-text-${n.id}`}>{n.text}</span>}
        {selected && <span className="absolute bottom-0 inset-x-0 bg-[#FF3B30] text-white text-[9px] font-black text-center">선택됨</span>}
      </button>
      <div className="flex gap-0.5">
        <button type="button" onClick={img ? onReplace : onEdit} disabled={busy} className="p-1 rounded border border-black/10"
                aria-label={img ? '교체' : '수정'} data-testid={`external-notice-${img ? 'replace' : 'edit'}-${n.id}`}>
          {img ? <RefreshCw size={10} /> : <Pencil size={10} />}
        </button>
        <button type="button" onClick={onDelete} className="p-1 rounded border border-black/10" aria-label="삭제" data-testid={`external-notice-delete-${n.id}`}><Trash2 size={10} /></button>
      </div>
    </div>
  );
};

export const ExternalNoticeList = ({ ctx, apply, showTitle = true, connected = false }) => {
  const input = useRef(null);
  const target = useRef(null);
  const [busy, setBusy] = useState(false);
  const [textMode, setTextMode] = useState(null);
  const list = ctx?.external_notices || [];
  const sel = ctx?.external_notice_selected || null;
  const full = list.length >= PRESET_MAX;
  const blockFull = () => { toast.warning(`외부송출용 공지는 텍스트·이미지 합쳐 최대 ${PRESET_MAX}개예요.`); };
  const pickImage = (id) => {
    if (!id && full) { blockFull(); return; }
    target.current = id;
    input.current?.click();
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      const id = target.current;
      apply((c) => (id ? presetOps.replaceNotice(c, id, url) : presetOps.addNotice(c, url)));
    } catch (err) { toast.error(err?.response?.data?.detail || '이미지 업로드 실패'); }
    finally { setBusy(false); }
  };
  const editing = textMode && textMode !== 'new' ? list.find((n) => n.id === textMode) : null;
  return (
    <div className="space-y-1.5" data-testid="external-notice-list">
      <div className="flex items-center gap-1">
        {showTitle && <p className="text-[11px] font-black inline-flex items-center gap-1" data-testid="external-notice-title"><Megaphone size={12} /> 외부송출용 공지 · {list.length}/{PRESET_MAX}</p>}
        {sel && (
          <button type="button" onClick={() => apply((c) => presetOps.selectNotice(c, null))}
                  className="ml-auto px-2 py-0.5 rounded-full border border-black/10 text-[10px] font-bold inline-flex items-center gap-0.5"
                  data-testid="external-notice-clear"><X size={10} />선택 해제</button>
        )}
      </div>
      <p className="text-[10px] text-gray-500 leading-tight" data-testid="external-notice-hint">외부 LIVE용 텍스트·이미지 공지</p>
      <p className="text-[9px] text-gray-400 leading-tight" data-testid="external-notice-pending">{connected ? '선택한 공지는 외부 LIVE 영상에만 표시돼요' : '실제 외부송출 반영은 연동 예정'}</p>
      <div className="flex flex-wrap gap-1.5">
        {list.map((n) => (
          <NoticeItem key={n.id} n={n} selected={sel === n.id} busy={busy}
                      onSelect={() => apply((c) => presetOps.selectNotice(c, n.id))}
                      onEdit={() => setTextMode(n.id)} onReplace={() => pickImage(n.id)}
                      onDelete={() => apply((c) => presetOps.removeNotice(c, n.id))} />
        ))}
      </div>
      {textMode === 'new' && (
        <TextForm testid="external-notice-new" onCancel={() => setTextMode(null)}
                  onSave={(t) => { apply((c) => presetOps.addTextNotice(c, t)); setTextMode(null); }} />
      )}
      {editing && (
        <TextForm testid={`external-notice-edit-${editing.id}`} init={editing.text} onCancel={() => setTextMode(null)}
                  onSave={(t) => { apply((c) => presetOps.updateTextNotice(c, editing.id, t)); setTextMode(null); }} />
      )}
      <div className="flex gap-1">
        <button type="button" onClick={() => (full ? blockFull() : setTextMode('new'))}
                className={`${chip} ${full ? 'opacity-50' : ''}`} data-testid="external-notice-add-text"><Plus size={12} />텍스트</button>
        <button type="button" onClick={() => pickImage(null)} disabled={busy}
                className={`${chip} ${full ? 'opacity-50' : ''}`} data-testid="external-notice-add"><Plus size={12} />{busy ? '업로드 중' : '이미지'}</button>
      </div>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="external-notice-file" />
    </div>
  );
};

// 외부송출용 공지 카드 — 타이머와 분리된 별도 영역 (모바일은 접고 펼침).
export const ExternalNoticeCard = ({ ctx, apply, collapsible = false, connected = false }) => (collapsible ? (
  <details open className="rounded-xl border border-black/10 dark:border-white/10 p-2" data-testid="external-notice-card">
    <summary className="text-[11px] font-black cursor-pointer select-none" data-testid="external-notice-card-toggle">
      <Megaphone size={12} className="inline mr-1" />외부송출용 공지 · {(ctx?.external_notices || []).length}/{PRESET_MAX}
    </summary>
    <div className="mt-1.5"><ExternalNoticeList ctx={ctx} apply={apply} showTitle={false} connected={connected} /></div>
  </details>
) : (
  <div className="rounded-xl border border-black/10 dark:border-white/10 p-2" data-testid="external-notice-card">
    <ExternalNoticeList ctx={ctx} apply={apply} connected={connected} />
  </div>
));

export const LivePresetPanel = ({ ctx, apply, canStart, collapsible = false, onStartTimer, connected = false }) => (
  <div className="space-y-2" data-testid="live-preset-panel">
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-2" data-testid="timer-preset-card">
      <TimerPresetList ctx={ctx} apply={apply} canStart={canStart} onStartTimer={onStartTimer} />
    </div>
    <ExternalNoticeCard ctx={ctx} apply={apply} collapsible={collapsible} connected={connected} />
  </div>
);

export default LivePresetPanel;
